import { useQuery } from '@tanstack/react-query'

import { appointmentApi } from '@services/appointment.service'
import { billingApi } from '@services/billing.service'
import { clinicalSafetyApi } from '@services/clinicalSafety.service'
import { patientApi } from '@services/patient.service'
import { pharmacyApi } from '@services/pharmacy.service'
import { getConsultationByAppointment } from '@services/consultation.service'
import { DEFAULT_CLINIC_TZ } from '@lib/clinicDate'
import {
  Appointment,
  ClinicalSafetyEvent,
  Invoice,
  LabResult,
  PaginatedResponse,
  Patient,
  Prescription,
} from '@/types'

type ActivityType = 'safety' | 'appointment' | 'lab' | 'prescription'

export interface DashboardActivity {
  id: string
  type: ActivityType
  title: string
  subtitle: string
  timestamp: string
  status: 'success' | 'pending' | 'critical'
}

export interface GlobalDashboardData {
  unavailableSources: DashboardDataSource[]
  totalPatients: number
  activePatients: number
  newPatientsThisMonth: number
  newPatientsThisWeek: number
  appointmentsToday: number
  scheduledAppointmentsToday: number
  avgAppointmentDurationMinutes: number
  pendingBillsCount: number
  pendingBillsAmount: number
  overdueInvoicesCount: number
  activePrescriptionsCount: number
  pendingLabResultsCount: number
  openSafetyEventsCount: number
  criticalSafetyEventsCount: number
  unsignedNotesCount: number
  unsignedNoteAppointments: Appointment[]
  nextAppointment: Appointment | null
  billedThisMonth: number
  billedLastMonth: number
  recentActivity: DashboardActivity[]
  todaysAppointments: Appointment[]
}

export type DashboardDataSource =
  | 'patientStats'
  | 'patients'
  | 'appointments'
  | 'invoices'
  | 'prescriptions'
  | 'labs'
  | 'safetyEvents'
  | 'consultations'

export interface PatientDashboardData {
  totalVisits: number
  openOrders: number
  activePrescriptionsCount: number
  balanceDue: number
  upcomingAppointments: Appointment[]
  recentActivity: DashboardActivity[]
  alerts: string[]
}

const PAGE_SIZE = 100

const PENDING_INVOICE_STATUSES = new Set(['pending', 'sent', 'partially_paid', 'overdue'])
const ACTIVE_PRESCRIPTION_STATUSES = new Set([
  'draft',
  'pending',
  'sent',
  'filled',
  'partially_filled',
  'ready_for_pickup',
  'picked_up',
  'delivered',
  'on_hold',
])
const PENDING_LAB_STATUSES = new Set(['ordered', 'sample_collected', 'in_progress', 'completed'])
const OPEN_SAFETY_STATUSES = new Set(['new', 'acknowledged', 'in_progress'])

const toNumber = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

// "Today" is reckoned in the clinic's active region: the calendar day boundary
// follows the clinical-availability timezone, so the agenda shifts when it changes.
const isToday = (isoDate: string, timeZone: string): boolean => {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone })
  return fmt.format(new Date(isoDate)) === fmt.format(new Date())
}

const isThisWeek = (isoDate: string): boolean => {
  const date = new Date(isoDate).getTime()
  const now = Date.now()
  return date >= now - 7 * 24 * 60 * 60 * 1000 && date <= now
}

const isThisCalendarMonth = (isoDate: string): boolean => {
  const d = new Date(isoDate)
  const now = new Date()
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
}

const isLastCalendarMonth = (isoDate: string): boolean => {
  const d = new Date(isoDate)
  const now = new Date()
  const lm = now.getMonth() === 0 ? 11 : now.getMonth() - 1
  const ly = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
  return d.getFullYear() === ly && d.getMonth() === lm
}

const fetchAllPages = async <T>(
  fetchPage: (skip: number, limit: number) => Promise<PaginatedResponse<T>>
): Promise<T[]> => {
  const first = await fetchPage(0, PAGE_SIZE)
  const allItems = [...first.items]
  const total = first.total ?? first.items.length
  if (allItems.length >= total) return allItems

  let skip = allItems.length
  let safetyCounter = 0
  while (skip < total && safetyCounter < 1000) {
    const page = await fetchPage(skip, PAGE_SIZE)
    if (!page.items.length) break
    allItems.push(...page.items)
    skip += page.items.length
    safetyCounter += 1
  }
  return allItems
}

const sortByDateDesc = <T>(items: T[], selector: (item: T) => string | undefined): T[] => {
  return [...items].sort((a, b) => {
    const aTs = selector(a) ? new Date(selector(a) as string).getTime() : 0
    const bTs = selector(b) ? new Date(selector(b) as string).getTime() : 0
    return bTs - aTs
  })
}

const normalizeActivity = (
  safetyEvents: ClinicalSafetyEvent[],
  appointments: Appointment[],
  labs: LabResult[],
  prescriptions: Prescription[]
): DashboardActivity[] => {
  const activities: DashboardActivity[] = []

  for (const event of safetyEvents) {
    activities.push({
      id: `safety-${event.id}`,
      type: 'safety',
      title: event.title,
      subtitle: event.description || `Safety event (${event.severity})`,
      timestamp: event.created_at,
      status: event.severity === 'critical' ? 'critical' : 'pending',
    })
  }

  for (const appointment of appointments) {
    activities.push({
      id: `appointment-${appointment.id}`,
      type: 'appointment',
      title: `Appointment ${appointment.status}`,
      subtitle: appointment.reason || appointment.appointment_type,
      timestamp: appointment.appointment_date,
      status: appointment.status === 'completed' ? 'success' : 'pending',
    })
  }

  for (const lab of labs) {
    activities.push({
      id: `lab-${lab.id}`,
      type: 'lab',
      title: `Lab result ${lab.status}`,
      subtitle: lab.test_name,
      timestamp: lab.completed_date || lab.ordered_date,
      status: lab.status === 'reviewed' ? 'success' : 'pending',
    })
  }

  for (const prescription of prescriptions) {
    activities.push({
      id: `prescription-${prescription.id}`,
      type: 'prescription',
      title: `Prescription ${prescription.status}`,
      subtitle: prescription.medication_name,
      timestamp: prescription.prescribed_date,
      status: prescription.status === 'filled' ? 'success' : 'pending',
    })
  }

  return sortByDateDesc(activities, (item) => item.timestamp).slice(0, 10)
}

export function useGlobalDashboardData(timezone: string = DEFAULT_CLINIC_TZ) {
  return useQuery({
    queryKey: ['dashboard-data', 'global', timezone],
    staleTime: 60_000,
    queryFn: async (): Promise<GlobalDashboardData> => {
      const [statsResult, patientsResult, appointmentsResult, invoicesResult, prescriptionsResult, labsResult, safetyResult] =
        await Promise.allSettled([
          patientApi.getStatistics(),
          fetchAllPages<Patient>((skip, limit) => patientApi.getPatients({ skip, limit })),
          fetchAllPages<Appointment>((skip, limit) => appointmentApi.getAppointments({ skip, limit })),
          fetchAllPages<Invoice>((skip, limit) => billingApi.getInvoices({ skip, limit })),
          fetchAllPages<Prescription>((skip, limit) => pharmacyApi.getPrescriptions({ skip, limit })),
          fetchAllPages<LabResult>((skip, limit) => pharmacyApi.getLabResults({ skip, limit })),
          fetchAllPages<ClinicalSafetyEvent>((skip, limit) => clinicalSafetyApi.getEvents({ skip, limit })),
        ])

      const patients = patientsResult.status === 'fulfilled' ? patientsResult.value : []
      const appointments = appointmentsResult.status === 'fulfilled' ? appointmentsResult.value : []
      const invoices = invoicesResult.status === 'fulfilled' ? invoicesResult.value : []
      const prescriptions = prescriptionsResult.status === 'fulfilled' ? prescriptionsResult.value : []
      const labs = labsResult.status === 'fulfilled' ? labsResult.value : []
      const safetyEvents = safetyResult.status === 'fulfilled' ? safetyResult.value : []
      const stats = statsResult.status === 'fulfilled' ? statsResult.value : null
      const unavailableSources: DashboardDataSource[] = []
      if (statsResult.status === 'rejected') unavailableSources.push('patientStats')
      if (patientsResult.status === 'rejected') unavailableSources.push('patients')
      if (appointmentsResult.status === 'rejected') unavailableSources.push('appointments')
      if (invoicesResult.status === 'rejected') unavailableSources.push('invoices')
      if (prescriptionsResult.status === 'rejected') unavailableSources.push('prescriptions')
      if (labsResult.status === 'rejected') unavailableSources.push('labs')
      if (safetyResult.status === 'rejected') unavailableSources.push('safetyEvents')

      const activePatients = patients.filter((patient) => patient.status === 'active').length
      const appointmentsToday = appointments.filter((appointment) => isToday(appointment.appointment_date, timezone)).length
      const scheduledAppointmentsToday = appointments.filter(
        (appointment) => isToday(appointment.appointment_date, timezone) && appointment.status === 'scheduled'
      ).length
      const avgAppointmentDurationMinutes =
        appointments.length > 0
          ? Math.round(
              appointments.reduce((acc, item) => acc + toNumber(item.duration_minutes), 0) / appointments.length
            )
          : 0

      const pendingInvoices = invoices.filter(
        (invoice) =>
          PENDING_INVOICE_STATUSES.has(String(invoice.status)) && toNumber(invoice.balance_due) > 0
      )
      const pendingBillsAmount = pendingInvoices.reduce(
        (acc, item) => acc + toNumber(item.balance_due),
        0
      )

      const activePrescriptionsCount = prescriptions.filter((item) =>
        ACTIVE_PRESCRIPTION_STATUSES.has(String(item.status))
      ).length
      const pendingLabResultsCount = labs.filter((item) =>
        PENDING_LAB_STATUSES.has(String(item.status))
      ).length
      const openSafetyEvents = safetyEvents.filter((item) =>
        OPEN_SAFETY_STATUSES.has(String(item.status))
      )

      const todaysAppointments = sortByDateDesc(
        appointments.filter((a) => isToday(a.appointment_date, timezone)),
        (a) => a.appointment_date
      ).reverse()

      // Fetch consultations for today's appointments to detect unsigned notes.
      // Bounded by the count of today's appointments (typically < 30).
      const consultationChecks = await Promise.allSettled(
        todaysAppointments.map((a) => getConsultationByAppointment(a.id))
      )
      if (consultationChecks.some((result) => result.status === 'rejected')) {
        unavailableSources.push('consultations')
      }
      const unsignedNoteAppointments = todaysAppointments.filter((apt, i) => {
        const result = consultationChecks[i]
        if (result.status === 'rejected') return false
        const consultation = result.value
        // Unsigned = note started but not attested, or appointment completed with no note started
        if (consultation === null) return apt.status === 'completed'
        return !consultation.attested_by_name
      })

      const nowMs = Date.now()
      const nextAppointment =
        todaysAppointments.length === 0
          ? (appointments
              .filter(
                (a) =>
                  new Date(a.appointment_date).getTime() > nowMs &&
                  (a.status === 'scheduled' || a.status === 'checked_in')
              )
              .sort(
                (a, b) =>
                  new Date(a.appointment_date).getTime() - new Date(b.appointment_date).getTime()
              )[0] ?? null)
          : null

      const overdueInvoicesCount = invoices.filter(
        (i) => String(i.status).toLowerCase() === 'overdue'
      ).length

      const newPatientsThisWeek = patients.filter((p) => isThisWeek(p.created_at)).length

      const billedThisMonth = invoices
        .filter((i) => isThisCalendarMonth(i.invoice_date))
        .reduce((sum, i) => sum + toNumber(i.total_amount), 0)

      const billedLastMonth = invoices
        .filter((i) => isLastCalendarMonth(i.invoice_date))
        .reduce((sum, i) => sum + toNumber(i.total_amount), 0)

      return {
        unavailableSources,
        totalPatients: patients.length,
        activePatients,
        newPatientsThisMonth: stats?.new_patients_this_month ?? 0,
        newPatientsThisWeek,
        appointmentsToday,
        scheduledAppointmentsToday,
        avgAppointmentDurationMinutes,
        pendingBillsCount: pendingInvoices.length,
        pendingBillsAmount,
        overdueInvoicesCount,
        activePrescriptionsCount,
        pendingLabResultsCount,
        openSafetyEventsCount: openSafetyEvents.length,
        criticalSafetyEventsCount: openSafetyEvents.filter((item) => item.severity === 'critical').length,
        unsignedNotesCount: unsignedNoteAppointments.length,
        unsignedNoteAppointments,
        nextAppointment,
        billedThisMonth,
        billedLastMonth,
        recentActivity: normalizeActivity(safetyEvents, appointments, labs, prescriptions),
        todaysAppointments,
      }
    },
  })
}

export function usePatientDashboardData(patientId: string) {
  return useQuery({
    queryKey: ['dashboard-data', 'patient', patientId],
    enabled: !!patientId,
    staleTime: 60_000,
    queryFn: async (): Promise<PatientDashboardData> => {
      const [appointmentsResult, invoicesResult, prescriptionsResult, labsResult, safetyResult] =
        await Promise.allSettled([
          fetchAllPages<Appointment>((skip, limit) =>
            appointmentApi.getAppointments({ patient_id: patientId, skip, limit })
          ),
          fetchAllPages<Invoice>((skip, limit) =>
            billingApi.getInvoices({ patient_id: patientId, skip, limit })
          ),
          fetchAllPages<Prescription>((skip, limit) =>
            pharmacyApi.getPrescriptions({ patient_id: patientId, skip, limit })
          ),
          fetchAllPages<LabResult>((skip, limit) =>
            pharmacyApi.getLabResults({ patient_id: patientId, skip, limit })
          ),
          fetchAllPages<ClinicalSafetyEvent>((skip, limit) =>
            clinicalSafetyApi.getEvents({ patient_id: patientId, skip, limit })
          ),
        ])

      const appointments = appointmentsResult.status === 'fulfilled' ? appointmentsResult.value : []
      const invoices = invoicesResult.status === 'fulfilled' ? invoicesResult.value : []
      const prescriptions = prescriptionsResult.status === 'fulfilled' ? prescriptionsResult.value : []
      const labs = labsResult.status === 'fulfilled' ? labsResult.value : []
      const safetyEvents = safetyResult.status === 'fulfilled' ? safetyResult.value : []

      const now = new Date().getTime()
      const upcomingAppointments = sortByDateDesc(
        appointments.filter((item) => new Date(item.appointment_date).getTime() >= now),
        (item) => item.appointment_date
      ).reverse()

      const visits = appointments.filter((item) => item.status === 'completed').length
      const openOrders = labs.filter((item) => PENDING_LAB_STATUSES.has(String(item.status))).length
      const activePrescriptionsCount = prescriptions.filter((item) =>
        ACTIVE_PRESCRIPTION_STATUSES.has(String(item.status))
      ).length
      const balanceDue = invoices
        .filter((invoice) => PENDING_INVOICE_STATUSES.has(String(invoice.status)))
        .reduce((acc, item) => acc + toNumber(item.balance_due), 0)

      const openSafetyEvents = safetyEvents.filter((item) =>
        OPEN_SAFETY_STATUSES.has(String(item.status))
      )
      const criticalSafety = openSafetyEvents.filter((item) => item.severity === 'critical').length

      const alerts: string[] = []
      if (criticalSafety > 0) {
        alerts.push(`${criticalSafety} critical safety event(s) require immediate review.`)
      }
      if (openOrders > 0) {
        alerts.push(`${openOrders} lab order/result item(s) are pending review.`)
      }
      if (balanceDue > 0) {
        alerts.push(`Outstanding patient balance is $${balanceDue.toFixed(2)}.`)
      }

      return {
        totalVisits: visits,
        openOrders,
        activePrescriptionsCount,
        balanceDue,
        upcomingAppointments: upcomingAppointments.slice(0, 5),
        recentActivity: normalizeActivity(safetyEvents, appointments, labs, prescriptions),
        alerts,
      }
    },
  })
}
